import React, { useMemo } from 'react';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { useResolvedElement } from '@client/hooks/use-resolved-version.hooks';
import { useSelectedProductListElement } from '@features/products/use-product-list.hooks';
import { hasElementSelection } from '@features/proposals/helpers/proposal-selection.helpers';
import {
  useSelectedElementId,
  useSelectedVersion,
  useUIState,
} from '@client/store/ui';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { Page } from '@client/store/ui/ui.model';

import { ProductSidePanelContent } from './components/ProductSidePanelContent';
import { ElementSidePanelContent } from './components/ElementSidePanelContent';
import { SidePanelCloseButton } from './components/SidePanelCloseButton';
import { ProjectSidePanelInsights } from './components/ProjectSidePanelInsights';
import { ActionsList } from '@features/proposals/ActionsList';
import { useRawProjectParams } from '@client/hooks/use-router.hooks';
import { CandidateRowSidePanel } from '@features/csv-import';

const SHOW_SIDE_PANEL_PAGES: Page[] = [
  Page.Elements,
  Page.Diff,
  Page.Products,
  Page.Import,
];

type ProjectSidePanelProps = {
  /** Build view shows proposal actions on a separate tab. */
  hideProposalActions?: boolean;
};

export const ProjectSidePanel: React.FC<ProjectSidePanelProps> = ({
  hideProposalActions = false,
}) => {
  const { selectedPage } = useUIState('selectedPage');
  const selectedVersion = useSelectedVersion();
  const selectedElementId = useSelectedElementId();
  // Resolve from the id directly so a selection that only exists in the proposal-resolved tree
  // (e.g. children added by a proposal-scoped ElementRecipeApply, see DEV-3293) still finds its node
  // instead of returning undefined because the baseline tree has no such id.
  const resolvedSelectedElement = useResolvedElement(selectedElementId);
  const selectedProductListElement = useSelectedProductListElement();
  const selectedProposalId = useSelectedProposalId();
  const {
    data: { elementId },
  } = useRawProjectParams();

  const showCloseButton =
    selectedPage === Page.Products && elementId !== 'none';

  const showSidePanel =
    SHOW_SIDE_PANEL_PAGES.includes(selectedPage) && !!selectedVersion;

  const content = useMemo(() => {
    if (!showSidePanel) {
      return undefined;
    }
    if (!SHOW_SIDE_PANEL_PAGES.includes(selectedPage)) {
      return undefined;
    }
    // CSV import tab owns its own panel routing: it shows the candidate-row
    // mapping form, and — once a row materializes — the element panel with the
    // mapping form on top (DEV-3298).
    if (selectedPage === Page.Import) {
      return <CandidateRowSidePanel />;
    }
    // Prefer the element panel whenever the selection resolves in the tree.
    // Do not gate on selectedPage — the UI store can lag behind the URL/tab.
    const proposalScopeKey = selectedProposalId ?? 'baseline';
    if (isElement(resolvedSelectedElement)) {
      return (
        <ElementSidePanelContent
          key={proposalScopeKey}
          selectedElement={resolvedSelectedElement}
        />
      );
    }
    if (selectedProductListElement) {
      return (
        <ProductSidePanelContent
          key={proposalScopeKey}
          element={selectedProductListElement}
        />
      );
    }
    // Keep proposal actions visible only when no element row is selected in the URL.
    // The build panel shows this list on its own Actions tab instead.
    if (
      !hideProposalActions &&
      selectedProposalId &&
      !hasElementSelection(selectedElementId)
    ) {
      return <ActionsList proposalId={selectedProposalId} />;
    }
    if (!selectedVersion) {
      return undefined;
    }
    return <ProjectSidePanelInsights version={selectedVersion} />;
  }, [
    showSidePanel,
    selectedPage,
    resolvedSelectedElement,
    selectedVersion,
    selectedElementId,
    selectedProductListElement,
    selectedProposalId,
    hideProposalActions,
  ]);

  return (
    <>
      {showCloseButton && <SidePanelCloseButton />}
      {content}
    </>
  );
};
